import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Archive, ArchiveRestore } from "lucide-react";
import api from "@/lib/api";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatApiError } from "@/lib/errors";

export default function AdminClients() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({ name: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/clients");
      setRows(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const open = (c) => {
    setEditing(c);
    setForm(c ? { name: c.name, notes: c.notes || "" } : { name: "", notes: "" });
    setFormOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/admin/clients/${editing.id}`, form);
      else await api.post("/admin/clients", form);
      toast.success(editing ? "Client updated" : "Client added");
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    }
  };

  const archive = async (c) => {
    try {
      await api.patch(`/admin/clients/${c.id}`, { status: "archived" });
      toast.success("Archived");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Archive failed");
    }
  };

  const unarchive = async (c) => {
    try {
      await api.patch(`/admin/clients/${c.id}`, { status: "active" });
      toast.success("Re-activated");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Failed");
    }
  };

  const doDelete = async () => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    try {
      const { data } = await api.delete(`/admin/clients/${c.id}`);
      toast.success(data.mode === "archived" ? "Archived (entries exist)" : "Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Failed");
    }
  };

  return (
    <div data-testid="admin-clients" className="mx-auto w-full max-w-5xl">
      <Breadcrumbs />
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold text-slate-900">Clients</h2>
          <p className="mt-1 text-sm text-slate-500">
            {rows.length} total · {rows.filter((r) => r.status === "active").length} active. Names are unique (case-insensitive).
          </p>
        </div>
        <Button
          onClick={() => open(null)}
          data-testid="clients-create-btn"
          className="gap-2 bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
        >
          <Plus className="h-4 w-4" />New client
        </Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="pr-4 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4} className="py-10 text-center text-sm text-slate-500">Loading…</TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-10 text-center text-sm text-slate-500">No clients yet.</TableCell>
              </TableRow>
            )}
            {rows.map((c) => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                <TableCell className="pl-4 font-medium text-slate-900">{c.name}</TableCell>
                <TableCell>
                  {c.status === "active" ? (
                    <Badge className="bg-cargodash-primary-50 text-cargodash-primary-700 hover:bg-cargodash-primary-50">Active</Badge>
                  ) : (
                    <Badge variant="secondary">Archived</Badge>
                  )}
                </TableCell>
                <TableCell className="max-w-xs truncate text-xs text-slate-500">{c.notes || "—"}</TableCell>
                <TableCell className="pr-4 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => open(c)} data-testid={`client-edit-${c.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {c.status === "active" ? (
                      <Button variant="ghost" size="sm" onClick={() => archive(c)} data-testid={`client-archive-${c.id}`} className="gap-1 text-xs">
                        <Archive className="h-3.5 w-3.5" />Archive
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => unarchive(c)} data-testid={`client-unarchive-${c.id}`} className="gap-1 text-xs">
                        <ArchiveRestore className="h-3.5 w-3.5" />Activate
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setConfirm(c)}
                      data-testid={`client-delete-${c.id}`}
                      className="text-cargodash-alert-600 hover:bg-cargodash-alert-600/10 hover:text-cargodash-alert-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit client" : "Add client"}</DialogTitle>
            <DialogDescription>Names must be unique (case-insensitive) across active and archived clients.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="client-form-name"
                placeholder="e.g. Acme Logistics"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                data-testid="client-form-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                data-testid="client-form-submit"
                className="bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
              >
                {editing ? "Save" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{confirm?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Clients referenced by any calendar entries are archived instead of deleted so history is preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-cargodash-alert-600 hover:bg-cargodash-alert-600/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
